/**
 * @file TabDataset.jsx
 * @description TabDataset component for displaying and managing tab dataset.
 *              Every uploaded dataset row for the customer is shown, grouped by
 *              upload datetime in expand/collapse accordions (same pattern as TabTexts).
 * @component TabDataset
 * @exports TabDataset - Main component for displaying and managing tab dataset.
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";
import { getcustomerdataset } from "../../../services/customerinfoModule/historyServices";
import { toDecimal } from "../../../utils/utils";
import { useRegisterCsvExport } from "../../customerModule/customers/CsvExportContext.jsx";
import { DATASET_COLUMNS } from "../../customerModule/customers/customerExportConfig.js";

const CustomerDataset = ({ customerguid }) => {
  const [dataset, setDataset] = useState([]);
  const [loading, setLoading] = useState(false);

  // The screen groups records into accordions; the export flattens them to one
  // row per record with the upload datetime as the first column.
  useRegisterCsvExport({
    name: "dataset",
    columns: DATASET_COLUMNS,
    rows: dataset,
    enabled: !loading,
  });

  useEffect(() => {
    if (customerguid) fetchDataset();
  }, [customerguid]);

  const fetchDataset = async () => {
    setLoading(true);
    try {
      const data = await getcustomerdataset({ customerguid });
      setDataset(data || []);
    } catch (error) {
      console.error("Error fetching dataset:", error);
    } finally {
      setLoading(false);
    }
  };

  // MM/dd/yyyy hh:mm AM/PM
  const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return isoDate;
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hours = date.getHours();
    const mins = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${mm}/${dd}/${yyyy} ${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
  };

  const money = (v) =>
    v === null || v === undefined || v === "" ? "—" : `$${toDecimal(v)}`;
  const plain = (v) =>
    v === null || v === undefined || v === "" ? "—" : String(v);
  const days = (v) =>
    v === null || v === undefined || v === "" ? "—" : `${v} days`;

  // Rows of three fields each — same field set as before, just laid out as config.
  const FIELD_GROUPS = [
    [
      { label: "Past Due", key: "dayspastdue", fmt: days },
      { label: "Total Amount Due", key: "totalamountdue", fmt: money },
      { label: "Monthly Payment", key: "monthlypayment", fmt: money },
    ],
    [
      { label: "Extension Amount", key: "extamt", fmt: money },
      { label: "Extension Eligible", key: "exteligible", fmt: plain },
      { label: "Roll Amount", key: "rollamt", fmt: money },
    ],
    [
      { label: "Repo Amount", key: "repoamt", fmt: money },
      { label: "Repo Date", key: "repodate", fmt: formatDate },
      { label: "Last 4 CC", key: "ccdigits", fmt: plain },
    ],
    [
      { label: "Vehicle Own Amount", key: "vehicleownamt", fmt: money },
      { label: "No Payment Received Past", key: "no_payment_received_past", fmt: plain },
      { label: "No Promise Received Past", key: "no_promise_received_past", fmt: plain },
    ],
    [
      { label: "Most Recent Payment Date", key: "most_recent_payment_date", fmt: formatDate },
      { label: "Most Recent Payment Amount", key: "most_recent_payment_amount", fmt: money },
      { label: "Minimum Payment Amount", key: "minimum_payment_amount", fmt: money },
    ],
    [
      { label: "Minimum Promise Amount", key: "minimum_promise_amount", fmt: money },
    ],
  ];

  // group by uploaddate
  const grouped = dataset.reduce((acc, item) => {
    const key = item.uploaddate ?? "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const groupKeys = Object.keys(grouped).sort((a, b) => {
    const da = new Date(a).getTime();
    const db = new Date(b).getTime();
    if (Number.isNaN(da) || Number.isNaN(db)) return 0;
    return db - da; // newest first
  });

  const renderRecord = (item, recordIndex, recordCount) => (
    <Box key={recordIndex} sx={{ mb: recordIndex < recordCount - 1 ? 2 : 0 }}>
      {recordCount > 1 && (
        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mb: 0.5 }}>
          {`Record ${recordIndex + 1} of ${recordCount}`}
        </Typography>
      )}
      <TableContainer component={Paper}>
        {FIELD_GROUPS.map((group, groupIndex) => (
          <Table stickyHeader size="small" key={groupIndex}>
            <TableHead>
              <TableRow>
                {group.map((field) => (
                  <TableCell key={field.key} sx={{ width: 150 }}>
                    <b>{field.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {group.map((field) => (
                  <TableCell key={field.key}>{field.fmt(item[field.key])}</TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        ))}
      </TableContainer>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      {loading && <ProgressOverlay />}

      {groupKeys.length === 0 && !loading ? (
        <Alert severity="info">No dataset found</Alert>
      ) : (
        groupKeys.map((uploadDate, index) => (
          <Accordion key={uploadDate} sx={{ mb: 1 }} defaultExpanded={index === 0}>
            <AccordionSummary
              sx={{ display: "flex", alignItems: "center", flexDirection: "row-reverse" }}
              expandIcon={<ExpandMoreIcon />}
            >
              <Typography sx={{ ml: 1, fontSize: "0.875rem", flex: 1 }}>
                {`Uploaded - ${formatDate(uploadDate)}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {grouped[uploadDate].map((item, recordIndex) =>
                renderRecord(item, recordIndex, grouped[uploadDate].length)
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default CustomerDataset;